// First-party attribution capture for the app (app.storiz.ma).
//
// The ad click-ids (fbclid/ttclid/gclid) + utm_* only exist in the URL at the
// FIRST landing, and the real Meta/TikTok pixel cookies (_fbc/_fbp/_ttp) are
// set by the pixels on whichever surface the customer landed on. The customer
// bounces storiz.ma → app.storiz.ma (builds album) → back to storiz.ma to
// order, so by order-creation time these are gone.
//
// We persist a FIRST-TOUCH snapshot in a cookie scoped to the shared root
// domain `.storiz.ma` so BOTH surfaces (theme + app) read/write the same value.
// The theme's utm-tracker.liquid writes the identical cookie shape. First-touch
// wins: once `storiz_attr` exists we never overwrite it.
//
// Tiny + dependency-free on purpose.

const COOKIE_NAME = 'storiz_attr';
// ~400 days (browser ITP will cap a JS cookie to ~7d anyway; the durable record
// is the phone-keyed server cache — this cookie just bridges the same session).
const MAX_AGE = 34560000;

const URL_PARAMS = [
  'fbclid',
  'ttclid',
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

// Real pixel cookies set by Meta/TikTok pixels on this surface.
const PIXEL_COOKIES = ['_fbc', '_fbp', '_ttp'] as const;

export interface StorizAttribution {
  fbclid?: string;
  ttclid?: string;
  gclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbc?: string;
  fbp?: string;
  ttp?: string;
  referrer?: string;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'),
  );
  return m ? decodeURIComponent(m[1]) : null;
}

/** Read the persisted first-touch attribution, or null if none captured yet. */
export function readAttribution(): StorizAttribution | null {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? (obj as StorizAttribution) : null;
  } catch {
    return null;
  }
}

/**
 * Capture first-touch attribution into the shared `.storiz.ma` cookie.
 * FIRST-TOUCH WINS: no-op if `storiz_attr` already exists. Safe to call on
 * every app load.
 */
export function captureFirstTouch(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (readAttribution()) return; // first-touch already captured — never clobber

  const attr: StorizAttribution = {};
  let found = false;

  try {
    const sp = new URLSearchParams(window.location.search);
    for (const k of URL_PARAMS) {
      const v = sp.get(k);
      if (v) {
        (attr as Record<string, string>)[k] = v;
        found = true;
      }
    }
  } catch {
    /* ignore malformed query strings */
  }

  // Map _fbc/_fbp/_ttp pixel cookies → fbc/fbp/ttp.
  for (const c of PIXEL_COOKIES) {
    const v = readCookie(c);
    if (v) {
      (attr as Record<string, string>)[c.slice(1)] = v; // _fbc → fbc
      found = true;
    }
  }

  if (document.referrer) {
    attr.referrer = document.referrer;
    found = true;
  }

  if (!found) return; // nothing worth persisting

  try {
    const onStoriz = /(^|\.)storiz\.ma$/.test(window.location.hostname);
    const domain = onStoriz ? '; domain=.storiz.ma' : '';
    document.cookie =
      COOKIE_NAME +
      '=' +
      encodeURIComponent(JSON.stringify(attr)) +
      domain +
      '; path=/; max-age=' +
      MAX_AGE +
      '; SameSite=Lax';
  } catch {
    /* cookie write blocked — non-fatal */
  }
}
